import React, { useEffect, useState, useMemo } from "react";
import { Button } from "../../components/ui/button";
import { CirclePlus, DeleteIcon, Edit } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Separator } from "../../components/ui/separator";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import axios from "axios";
import { toast, Toaster } from "sonner";

const Spinner = () => (
  <div className="inline-block w-5 h-5 border-4 border-t-transparent border-gray-600 rounded-full animate-spin"></div>
);


const Product = () => {
  const token = localStorage.getItem("adminToken");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);

  const [formData, setFormData] = useState({
    category: "",
    product: "",
    brand: "",
    description: "",
    stocks: "",
    image: null,
    price: "",
    discount: "",
    productModal: "",
    hsn: "",
    gst: "",
    howtouse: "",
    specifications: "",
  });

  const [updateFormData, setUpdateFormData] = useState({
    category: "",
    product: "",
    brand: "",
    description: "",
    stocks: "",
    image: null,
    price: "",
    discount: "",
    productModal: "",
    hsn: "",
    gst: "",
    howtouse: "",
    specifications: "",
  });

  // Filter products based on search term
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    
    const lowercasedSearch = searchTerm.toLowerCase();
    return products.filter(product => 
      product.name.toLowerCase().includes(lowercasedSearch) ||
      (product.brand && product.brand.toLowerCase().includes(lowercasedSearch)) ||
      (product.category?.name && product.category.name.toLowerCase().includes(lowercasedSearch)) ||
      (product.description && product.description.toLowerCase().includes(lowercasedSearch)) ||
      (product.model && product.model.toLowerCase().includes(lowercasedSearch))
    );
  }, [products, searchTerm]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [productRes, categoryRes] = await Promise.all([
        axios.get("https://wavetechservices.in/api/admin/products", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(
          "https://wavetechservices.in/api/admin/services/service-categories",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      ]);
         setIsLoading(false);
      setProducts(productRes.data);
      setCategories(categoryRes.data.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setUpdateFormData((prevFormData) => ({
        ...prevFormData,
        [name]: files[0], 
      })); 
    } else {
      setUpdateFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: files[0],  
      }));
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const data = new FormData();
    data.append("category", formData.category);
    data.append("name", formData.product);
    data.append("brand", formData.brand);
    data.append("description", formData.description);
    data.append("price", formData.price);
    data.append("stock", formData.stocks);
    data.append("specifications", formData.specifications);
    data.append("howToUse", formData.howtouse);
    data.append("hsnCode", formData.hsn);
    data.append("gstPercentage", formData.gst);
    data.append("discountPercentage", formData.discount);
    data.append("model", formData.productModal);
    if (formData.image) {
      data.append("image", formData.image);
    }

    try {
      await axios.post(
        "https://wavetechservices.in/api/admin/products",
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Product Added successfully");
      fetchData();
         setIsLoading(false);
      setOpenAdd(false);
      setFormData({
        category: "",
        product: "",
        brand: "",
        description: "",
        stocks: "",
        image: null,
        price: "",
        discount: "",
        productModal: "",
        hsn: "",
        gst: "",
        howtouse: "",
        specifications: "",
      });
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.message || "Error adding product");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedProduct) return;
    setIsLoading(true);

    const formDataToSend = new FormData();
    formDataToSend.append("category", updateFormData.category);
    formDataToSend.append("name", updateFormData.product);
    formDataToSend.append("brand", updateFormData.brand);
    formDataToSend.append("description", updateFormData.description);
    formDataToSend.append("price", updateFormData.price);
    formDataToSend.append("stock", updateFormData.stocks);
    formDataToSend.append("specifications", updateFormData.specifications);
    formDataToSend.append("howToUse", updateFormData.howtouse);
    formDataToSend.append("hsnCode", updateFormData.hsn);
    formDataToSend.append("gstPercentage", updateFormData.gst);
    formDataToSend.append("discountPercentage", updateFormData.discount);
    formDataToSend.append("model", updateFormData.productModal);
    if (updateFormData.image) {
      formDataToSend.append("image", updateFormData.image);
    }

    try {
      await axios.put(
        `https://wavetechservices.in/api/admin/products/${selectedProduct._id}`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Product Updated successfully");
      fetchData();
      setOpenEdit(false);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.response?.data?.message || "Error updating product");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProduct) return;
    setIsLoading(true);

    try {
      await axios.delete(
        `https://wavetechservices.in/api/admin/products/${selectedProduct._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Product Deleted successfully");
      fetchData();
      setOpenDelete(false);
    } catch (err) {
      console.error(err, "error");
      toast.error("Failed to delete product");
    } finally {
      setIsLoading(false);
    }
  };

  // Open edit dialog with product data
  const openEditDialog = (product) => {
    setSelectedProduct(product);
    setUpdateFormData({
      category: product.category?._id || "",
      product: product.name,
      brand: product.brand || "",
      description: product.description || "",
      stocks: product.stock,
      image: null,
      price: product.price,
      discount: product.discountPercentage || "",
      productModal: product.model || "",
      hsn: product.hsnCode || "",
      gst: product.gstPercentage || "",
      howtouse: product.howToUse || "",
      specifications: product.specifications || "",
    });
    setOpenEdit(true);
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Calculate pagination based on filtered products
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="px-[1vw] my-5">
      <Toaster/>
      <div className="flex lg:justify-between lg:items-center flex-col lg:flex-row ">
        <h1 className="text-2xl">Products Management</h1>
        <div className="flex lg:gap-4 items-center gap-2 my-3 lg:my-0">
          <Input 
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="min-w-[200px]"
          />

<Dialog open={openAdd} onOpenChange={setOpenAdd}>
            <DialogTrigger>
              <Button className="bg-black px-5 text-white h-9 hover:opacity-90">
                Add <CirclePlus className="size-4 ml-1" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px] max-h-[760px] overflow-y-auto">
              <DialogHeader className="space-y-6">
                <DialogTitle className="lg:text-2xl text-xl">
                  Add Product
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex items-center gap-4">
                  <Label htmlFor="category" className="w-44">
                    Category
                  </Label>
                  <Select
                    onValueChange={(value) =>
                      setFormData({ ...formData, category: value })
                    }
                    value={formData.category}
                    required
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category._id} value={category._id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="product" className="w-44">
                    Product Name
                  </Label>
                  <Input
                    id="product"
                    name="product"
                    type="text"
                    value={formData.product}
                    onChange={handleChange}
                    className="col-span-3"
                    required
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="brand" className="w-44">
                    Brand Name
                  </Label>
                  <Input
                    id="brand"
                    name="brand"
                    type="text"
                    value={formData.brand}
                    onChange={handleChange}
                    className="col-span-3"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="description" className="w-44">
                    Description
                  </Label>
                  <Input
                    id="description"
                    name="description"
                    type="text"
                    value={formData.description}
                    onChange={handleChange}
                    className="col-span-3"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="stocks" className="w-44">
                    Stocks
                  </Label>
                  <Input
                    id="stocks"
                    name="stocks"
                    type="number"
                    value={formData.stocks}
                    onChange={handleChange}
                    className="col-span-3"
                    required
                    min="0"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="image" className="w-44">
                    Image
                  </Label>
                  <Input
                    id="image"
                    name="image"
                    type="file"
                    onChange={handleChange}
                    className="col-span-3"
                    accept="image/*"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="price" className="w-44">
                    Price
                  </Label>
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleChange}
                    className="col-span-3"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="discount" className="w-44">
                    Discount (%)
                  </Label>
                  <Input
                    id="discount"
                    name="discount"
                    type="number"
                    value={formData.discount}
                    onChange={handleChange}
                    className="col-span-3"
                    min="0"
                    max="100"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="productModal" className="w-44">
                    Product Model
                  </Label>
                  <Input
                    id="productModal"
                    name="productModal"
                    type="text"
                    value={formData.productModal}
                    onChange={handleChange}
                    className="col-span-3"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="hsn" className="w-44">
                    HSN Code
                  </Label>
                  <Input
                    id="hsn"
                    name="hsn"
                    type="text"
                    value={formData.hsn}
                    onChange={handleChange}
                    className="col-span-3"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="gst" className="w-44">
                    GST (%)
                  </Label>
                  <Input
                    id="gst"
                    name="gst"
                    type="number"
                    value={formData.gst}
                    onChange={handleChange}
                    className="col-span-3"
                    min="0"
                    max="100"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="howtouse" className="w-44">
                    How To Use
                  </Label>
                  <Input
                    id="howtouse"
                    name="howtouse"
                    type="text"
                    value={formData.howtouse}
                    onChange={handleChange}
                    className="col-span-3"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label htmlFor="specifications" className="w-44">
                    Specifications
                  </Label>
                  <Input
                    id="specifications"
                    name="specifications"
                    type="text"
                    value={formData.specifications}
                    onChange={handleChange}
                    className="col-span-3"
                  />
                </div>

                <DialogFooter>
                  <Button
                    type="submit"
                    className="bg-black px-5 text-white h-9 hover:opacity-90 flex items-center justify-center gap-2"
                    disabled={isLoading}
                  >
                   
                    {isLoading ? (
                    <>
                      <Spinner />
                      Adding...
                    </>
                  ) : (
                    "Add Product"
                  )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <Separator className="my-4" />

      <div className="overflow-x-auto lg:max-w-full max-w-full md:max-w-full h-[70vh]">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <p>Loading products...</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">Sl No.</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Product Name</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead className="w-[150px]">Description</TableHead>
                <TableHead>Stocks</TableHead>
                <TableHead>Image</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>HSN Code</TableHead>
                <TableHead>GST</TableHead>
                <TableHead className="w-[150px]">How To Use</TableHead>
                <TableHead className="w-[150px]">Specifications</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentItems.length > 0 ? (
                currentItems.map((product, index) => (
                  <TableRow key={product._id}>
                    <TableCell>{indexOfFirstItem + index + 1}</TableCell>
                    <TableCell>{product.category?.name || "N/A"}</TableCell>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell>{product.brand || "N/A"}</TableCell>
                    <TableCell className="truncate max-w-[150px]">
                      {product.description || "N/A"}
                    </TableCell>
                    <TableCell>{product.stock}</TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Product Image</DialogTitle>
                            <DialogDescription>
                              <div className="flex justify-center my-4">
                                {product.image ? (
                                  <img
                                    src={`${product.image}`}
                                    alt={product.name}
                                    className="max-h-[400px] rounded-md"
                                  />
                                ) : (
                                  <p>No image available</p>
                                )}
                              </div>
                            </DialogDescription>
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                    <TableCell>₹{product.price}</TableCell>
                    <TableCell>{product.discountPercentage || "0"}%</TableCell>
                    <TableCell>{product.model || "N/A"}</TableCell>
                    <TableCell>{product.hsnCode || "N/A"}</TableCell>
                    <TableCell>{product.gstPercentage || "0"}%</TableCell>
                    <TableCell className="truncate max-w-[150px]">
                      {product.howToUse || "N/A"}
                    </TableCell>
                    <TableCell className="truncate max-w-[150px]">
                      {product.specifications || "N/A"}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(product)}
                          disabled={isLoading}
                        >
                          <Edit className="text-blue-500 size-4" />
                        </Button>
                        <Dialog open={openDelete} onOpenChange={setOpenDelete}>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setSelectedProduct(product)}
                              disabled={isLoading}
                            >
                              <DeleteIcon className="text-red-500 size-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Confirm Deletion</DialogTitle>
                              <DialogDescription>
                                Are you sure you want to delete the product "{selectedProduct?.name}"?
                                This action cannot be undone.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <Button
                                variant="destructive"
                                onClick={handleDelete}
                                disabled={isLoading}
                                className="flex items-center justify-center gap-2"
                              >
                                {isLoading ?<>
                                <Spinner/>
                                Deleting
                                </> : "Delete"}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={15} className="text-center py-8">
                    {searchTerm ? "No matching products found" : "No products available"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Edit Product Dialog */}
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent className="sm:max-w-[425px] max-h-[760px] overflow-y-auto">
          <DialogHeader className="space-y-6">
            <DialogTitle className="lg:text-2xl text-xl">
              Update Product
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={(e) => { e.preventDefault(); handleUpdate(); }} className="space-y-4">
            <div className="flex items-center gap-4">
              <Label htmlFor="edit-category" className="w-44">
                Category
              </Label>
              <Select
                onValueChange={(value) =>
                  setUpdateFormData({ ...updateFormData, category: value })
                }
                value={updateFormData.category}
                required
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category._id} value={category._id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-4">
              <Label htmlFor="edit-product" className="w-44">
                Product Name
              </Label>
              <Input
                id="edit-product"
                name="product"
                type="text"
                value={updateFormData.product}
                onChange={handleUpdateChange}
                className="col-span-3"
                required
              />
            </div>

            <div className="flex items-center gap-4">
              <Label htmlFor="edit-brand" className="w-44">
                Brand Name
              </Label>
              <Input
                id="edit-brand"
                name="brand"
                type="text"
                value={updateFormData.brand}
                onChange={handleUpdateChange}
                className="col-span-3"
              />
            </div>

            <div className="flex items-center gap-4">
              <Label htmlFor="edit-description" className="w-44">
                Description
              </Label>
              <Input
                id="edit-description"
                name="description"
                type="text"
                value={updateFormData.description}
                onChange={handleUpdateChange}
                className="col-span-3"
              />
            </div>

            <div className="flex items-center gap-4">
              <Label htmlFor="edit-stocks" className="w-44">
                Stocks
              </Label>
              <Input
                id="edit-stocks"
                name="stocks"
                type="number"
                value={updateFormData.stocks}
                onChange={handleUpdateChange}
                className="col-span-3"
                required
                min="0"
              />
            </div>

            <div className="flex items-center gap-4">
              <Label htmlFor="edit-image" className="w-44">
                Image
              </Label>
              <Input
                id="edit-image"
                name="image"
                type="file"
                onChange={handleUpdateChange}
                className="col-span-3"
                accept="image/*"
              />
            </div>

            <div className="flex items-center gap-4">
              <Label htmlFor="edit-price" className="w-44">
                Price
              </Label>
              <Input
                id="edit-price"
                name="price"
                type="number"
                value={updateFormData.price}
                onChange={handleUpdateChange}
                className="col-span-3"
                required
                min="0"
                step="0.01"
              />
            </div>

            <div className="flex items-center gap-4">
              <Label htmlFor="edit-discount" className="w-44">
                Discount (%)
              </Label>
              <Input
                id="edit-discount"
                name="discount"
                type="number"
                value={updateFormData.discount}
                onChange={handleUpdateChange}
                className="col-span-3"
                min="0"
                max="100"
              />
            </div>

            <div className="flex items-center gap-4">
              <Label htmlFor="edit-productModal" className="w-44">
                Product Model
              </Label>
              <Input
                id="edit-productModal"
                name="productModal"
                type="text"
                value={updateFormData.productModal}
                onChange={handleUpdateChange}
                className="col-span-3"
              />
            </div>

            <div className="flex items-center gap-4">
              <Label htmlFor="edit-hsn" className="w-44">
                HSN Code
              </Label>
              <Input
                id="edit-hsn"
                name="hsn"
                type="text"
                value={updateFormData.hsn}
                onChange={handleUpdateChange}
                className="col-span-3"
              />
            </div>

            <div className="flex items-center gap-4">
              <Label htmlFor="edit-gst" className="w-44">
                GST (%)
              </Label>
              <Input
                id="edit-gst"
                name="gst"
                type="number"
                value={updateFormData.gst}
                onChange={handleUpdateChange}
                className="col-span-3"
                min="0"
                max="100"
              />
            </div>

            <div className="flex items-center gap-4">
              <Label htmlFor="edit-howattouse" className="w-44">
                How To Use
              </Label>
              <Input
                id="edit-howattouse"
                name="howtouse"
                type="text"
                value={updateFormData.howtouse}
                onChange={handleUpdateChange}
                className="col-span-3"
              />
            </div>

            <div className="flex items-center gap-4">
              <Label htmlFor="edit-specifications" className="w-44">
                Specifications
              </Label>
              <Input
                id="edit-specifications"
                name="specifications"
                type="text"
                value={updateFormData.specifications}
                onChange={handleUpdateChange}
                className="col-span-3"
              />
            </div>

            <DialogFooter>
              <Button
                type="submit"
                className="bg-black px-5 text-white h-9 hover:opacity-90"
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update Product"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {filteredProducts.length > itemsPerPage && (
        <div className="mt-4">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                />
              </PaginationItem>

              {Array.from({ length: totalPages }).map((_, index) => {
                const page = index + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        isActive={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                }
                return null;
              })}

              <PaginationItem>
                <PaginationNext 
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default Product;